import { Button } from "@chakra-ui/button";
import { Box } from "@chakra-ui/layout";
import { Col, Row, SelectProps } from "antd";
import useGetRateios from "core/features/rateio/hooks/useGetRateios";
import { RateioItem } from "core/features/rateio/typings";
import colors from "core/resources/theme/colors";
import { useEffect, useMemo, useState } from "react";
import { BiEdit } from "react-icons/bi";
import SelectBaseAntd from "ui/components/Input/SelectBaseAntd";
import ModalFormRateio from "./ModalFormRateio";

export interface SelectRateioProps<T = any> extends SelectProps<T> {
  value: any;
  onChange: (v: any) => void;
  isInvalid?: boolean;
  errorMessage?: string;
  month: string;
  year: string;
  onSelectRateio?: (rateio: RateioItem | undefined) => any;
}

function SelectRateio({
  value,
  onChange,
  isInvalid,
  errorMessage,
  month,
  year,
  onSelectRateio,
  ...rest
}: SelectRateioProps) {
  const { isLoading, isFetching, data, refetch } = useGetRateios({
    year,
    month,
  });
  const [rateioEditId, setRateioEditId] = useState<string | undefined>(
    undefined
  );
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [search, setSearch] = useState<string>();

  useEffect(() => {
    if (!isOpenModal) {
      setSearch("");
      setRateioEditId(undefined);
    }
  }, [isOpenModal]);

  useEffect(() => {
    if (rateioEditId) {
      setIsOpenModal(true);
    }
  }, [rateioEditId]);

  const _rateio = useMemo(
    () => data?.find((r) => r.id === value),
    [data, value]
  );

  useEffect(() => {
    onSelectRateio?.(_rateio);
  }, [_rateio, onSelectRateio]);

  return (
    <>
      {isOpenModal ? (
        <ModalFormRateio
          isOpenModal={isOpenModal}
          month={month}
          year={year}
          onClose={() => setIsOpenModal(false)}
          nomeRateio={_rateio?.nome}
          rateioId={rateioEditId}
          rateioAberto={_rateio?.aberto}
          onSuccess={async (id) => {
            onChange(id);

            setIsOpenModal(false);

            await refetch();
          }}
        />
      ) : null}

      <Box as={Row} gutter={24} alignItems="center">
        <Col sm={19}>
          <SelectBaseAntd
            isInvalid={isInvalid}
            loading={isLoading || isFetching}
            size="large"
            placeholder="Selecione o rateio"
            style={{ width: "100%", flexGrow: 1, flex: 1 }}
            value={value || undefined}
            onChange={onChange}
            {...rest}
            showSearch
            allowClear={false}
            searchValue={search}
            onSearch={(v) => {
              if (!isOpenModal) {
                setSearch(v);
              }
            }}
            optionFilterProp="label"
            options={data?.map((item) => ({
              value: item.id,
              label: item.nome,
            }))}
            notFoundContent={
              <Box>
                Nenhum rateio encontrado{" "}
                <Button
                  leftIcon={<i className="mdi mdi-plus" />}
                  size="xs"
                  colorScheme="orange"
                  onClick={() => setIsOpenModal(true)}
                >
                  cadastrar novo
                </Button>
              </Box>
            }
          />
        </Col>

        <Box as={Col} sm={5}>
          {!!value ? (
            <BiEdit
              color={colors.primary}
              size={24}
              onClick={() => setRateioEditId(value)}
              cursor="pointer"
              aria-label="Editar rateio"
              aria-roledescription="Botão de editar rateio"
              style={{ marginTop: "auto", marginBottom: "auto" }}
            />
          ) : null}
        </Box>
      </Box>
    </>
  );
}

export default SelectRateio;
